import {
  A11Y_PREFERENCE_DEFAULTS,
  A11Y_PREFERENCE_KEYS,
  A11Y_PREFERENCES_KEY,
  A11yPreferenceKey,
  A11yPreferences,
  createDefaultA11yPreferences,
  isA11yPreferenceValue,
} from './a11y-preferences';

/**
 * MAGAZYN PREFERENCJI — trwały MIĘDZY WIZYTAMI, więc `localStorage`, nie `sessionStorage`.
 *
 * Kształt skopiowany z `src/app/features/course-signup/signup-draft.ts` (dostęp przez
 * `globalThis` w `try/catch`, interfejs magazynu jako typ, odczyt jako `unknown` i merge na
 * świeży model). API jest inne CELOWO: tamten moduł pamięta jedną kartę i umiera razem z nią,
 * ten ma przetrwać zamknięcie przeglądarki — to jest wymóg z §5.2 kontraktu fazy, a nie
 * przeoczenie. Zdanie „this module never touches localStorage" w tamtym pliku pozostaje
 * prawdziwe: dotyczy TAMTEGO modułu, który go nadal nie tyka.
 *
 * ZAPISANY TEKST JEST WEJŚCIEM NIEZAUFANYM — klient może go przepisać w devtools. Dlatego:
 * czytamy jako `unknown`, scalamy na świeży model domyślny, a wartość spoza słownika
 * traktujemy jak BRAK (czyli domyślną), nigdy jak błąd. Zepsuty JSON nie wywala ekranu.
 *
 * KLUCZE DOMYŚLNE NIE SĄ ZAPISYWANE. Preferencja ustawiona z powrotem na wartość domyślną
 * znika z magazynu — to jest dosłownie „reset per opcja" z §5.1 kontraktu („usunięcie klucza
 * z modelu"), a przy okazji sprawia, że pusty obiekt i brak wpisu znaczą to samo.
 */

/** Wycinek `Storage`, którego ten moduł używa — nazwany strukturalnie, żeby spec podał atrapę. */
export interface A11yPreferencesStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Zapis jednej pary do modelu.
 *
 * Wydzielone w generyk, bo w pętli po WSZYSTKICH kluczach `preferences[key]` ma typ `never`
 * po stronie ZAPISU: TypeScript nie potrafi zagwarantować, że wartość z unii pasuje do klucza
 * z unii. Generyk wiąże jedno z drugim i przywraca kontrolę typów zamiast ją wyłączać rzutowaniem.
 */
function assignPreference<K extends A11yPreferenceKey>(
  preferences: A11yPreferences,
  key: K,
  value: A11yPreferences[K],
): void {
  preferences[key] = value;
}

/**
 * Magazyn tej przeglądarki albo `null`, gdy go nie ma.
 *
 * Dwa realne przypadki odpowiadają `null`: serwer (SSR — `provideClientHydration()` jest
 * włączone, a na serwerze nie ma `localStorage`) i przeglądarka rzucająca na samej
 * właściwości w trybie prywatnym. Oba znaczą „bez zapamiętywania preferencji", co kosztuje
 * ponowne ustawienie — nigdy ekran błędu.
 */
export function browserA11yPreferencesStorage(): A11yPreferencesStorage | null {
  try {
    return (globalThis.localStorage as A11yPreferencesStorage | undefined) ?? null;
  } catch {
    return null;
  }
}

export function readA11yPreferences(storage: A11yPreferencesStorage | null): A11yPreferences {
  const preferences = createDefaultA11yPreferences();

  if (storage === null) {
    return preferences;
  }

  let raw: string | null;

  try {
    raw = storage.getItem(A11Y_PREFERENCES_KEY);
  } catch {
    // Kwota, tryb prywatny, wyłączony magazyn — jedna nazwana klasa porażki: „nie ma zapisu".
    return preferences;
  }

  if (raw === null || raw === '') {
    return preferences;
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    return preferences;
  }

  if (!isRecord(parsed)) {
    return preferences;
  }

  for (const key of A11Y_PREFERENCE_KEYS) {
    const value = parsed[key];

    if (isA11yPreferenceValue(key, value)) {
      assignPreference(preferences, key, value);
    }
  }

  return preferences;
}

export function writeA11yPreferences(
  storage: A11yPreferencesStorage | null,
  preferences: A11yPreferences,
): void {
  if (storage === null) {
    return;
  }

  const stored: Record<string, string> = {};

  for (const key of A11Y_PREFERENCE_KEYS) {
    if (preferences[key] !== A11Y_PREFERENCE_DEFAULTS[key]) {
      stored[key] = preferences[key];
    }
  }

  try {
    if (Object.keys(stored).length === 0) {
      storage.removeItem(A11Y_PREFERENCES_KEY);
      return;
    }

    storage.setItem(A11Y_PREFERENCES_KEY, JSON.stringify(stored));
  } catch {
    // Zapis może paść na kwocie. Preferencja działa do końca sesji i nie przeżyje karty —
    // gorzej niż obiecywaliśmy, ale nadal lepiej niż wywalony ekran.
  }
}

/** Reset pojedynczej opcji: wartość domyślna, czyli po zapisie brak klucza w magazynie. */
export function resetA11yPreference(
  preferences: A11yPreferences,
  key: A11yPreferenceKey,
): A11yPreferences {
  return { ...preferences, [key]: A11Y_PREFERENCE_DEFAULTS[key] };
}
